import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { FiTrash2, FiRotateCw } from 'react-icons/fi';
import Table from '../shared/table/Table';
import { useNavigate } from 'react-router-dom';
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { confirmAlert } from "react-confirm-alert"; 
import { toast } from "react-toastify"; 

const TrashVersionsTable = () => {
  const [items, setItems] = useState([]);
  const [perms, setPerms] = useState([]);
  const navigate = useNavigate();

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (key, def) => translations[key] || def;

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const [trashRes, permRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/versions/trash/version`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/my-permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setItems(trashRes.data || []);
        setPerms(permRes.data.permissions || []);
      } catch (error) {
        console.error("Çöp kutusu verisi yüklenemedi:", error);
      }
    })();
  }, []);

  const modulePerm = perms.find(p => p.module_key === "static_admin_accounts") || {};
  const canRestore = modulePerm.full_access || modulePerm.edit_permission;
  const canDestroy = modulePerm.full_access || modulePerm.delete_permission;

  const handleRestore = (id) => {
    confirmAlert({
      title: t("RESTORE_CONFIRM_TITLE", "Versiyonu Geri Yükle"),
      message: t("RESTORE_CONFIRM_MSG", "Bu versiyonu geri yüklemek istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_RESTORE", "Evet, Geri Yükle"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/versions/restore/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(t("SUCCESS_VERSION_RESTORED", "Versiyon başarıyla geri yüklendi!"));
              setItems(prev => prev.filter(i => i.id !== id));
            } catch (err) {
              console.error("Geri yükleme hatası:", err);
              toast.error(t("ERROR_VERSION_RESTORE_FAILED", "Geri yükleme başarısız!"));
            }
          },
        },
        {
          label: t("CANCEL", "İptal"),
          onClick: () => toast.info(t("RESTORE_CANCELLED", "İşlem iptal edildi.")),
        },
      ],
    });
  };
  
  const handleDestroy = (id) => {
    confirmAlert({
      title: t("DELETE_CONFIRM_TITLE", "Versiyonu Kalıcı Sil"),
      message: t("DELETE_CONFIRM_MSG", "Bu versiyonu kalıcı olarak silmek istediğinize emin misiniz?"),
      buttons: [
        {
          label: t("YES_DELETE", "Evet, Sil"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/versions/permanent-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(t("SUCCESS_VERSION_DELETED", "Versiyon kalıcı olarak silindi!"));
              setItems(prev => prev.filter(i => i.id !== id));
            } catch (err) {
              console.error("Kalıcı silme hatası:", err);
              toast.error(t("ERROR_VERSION_DELETE_FAILED", "Kalıcı silme başarısız!"));
            }
          },
        },
        {
          label: t("CANCEL", "İptal"),
          onClick: () => toast.info(t("DELETE_CANCELLED", "İşlem iptal edildi.")),
        },
      ],
    });
  };

  const columns = useMemo(() => [
    { accessorKey: 'id', header: t("TABLE_ID_LABEL", "ID"), size: 60 },
    { accessorKey: 'title', header: t("TABLE_TITLE_LABEL", "Başlık"), size: 250 },

    {
      accessorKey: 'createdAt',
      header: t("TABLE_CREATED_AT", "Oluşturulma Tarihi"),
      meta: { headerClassName: 'text-end', className: 'text-end' },
      size: 150,
      cell: (info) => {
        const date = info.getValue();
        if (!date) return "-";
        return new Date(date).toLocaleDateString("tr-TR");
      }
    },

    {
      accessorKey: 'deleted_at',
      header: t("TABLE_DELETED_AT", "Silinme Tarihi"),
      meta: { headerClassName: 'text-end', className: 'text-end' },
      size: 150,
      cell: (info) => {
        const date = info.getValue();
        if (!date) return "-";
        return new Date(date).toLocaleDateString("tr-TR");
      }
    },

    {
      accessorKey: 'actions',
      header: t("TABLE_ACTIONS_LABEL", "İşlemler"),
      enableSorting: false,
      size: 140,
      meta: { headerClassName: 'text-end', className: 'text-end' },
      cell: (info) => {
        const id = info.row.original.id;
        return (
          <div className="d-flex gap-2 justify-content-end">
            {canRestore && (
              <button
                onClick={() => handleRestore(id)}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={t("RESTORE", "Geri Yükle")}
              >
                <FiRotateCw />
              </button>
            )}
            {canDestroy && (
              <button
                onClick={() => handleDestroy(id)}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={t("DELETE_PERMANENT", "Kalıcı Sil")}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        );
      }
    }
  ], [t, canRestore, canDestroy]);

  return <Table data={items} columns={columns} />;
};

export default TrashVersionsTable;
